import { EstimatedUnbillFigureConstant } from "presentation/constant/EstimatedUnbillFigure/EstimatedUnbillFigureConstant";
import { useEstimatedUnbillFigureVM } from "presentation/hook/EstimatedUnbillFigure/useEstimatedUnbillFigureVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useEstimatedUnbillFigureTracked } from "presentation/store/EstimatedUnbillFigure/EstimatedUnbillFigureProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { NbisIconButton } from "presentation/view/components/NbisIconButton";
import { memo, useCallback, useMemo, useState } from "react";
import { FieldType, HPHBreadcrumb, IconButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { Breadcrumb, SidebarCaption, Sidebarheader, SidebarTitle, StyledAction, StyledSidebar } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const EstimatedUnbillFigureEditPanel = () => {
    const ESTIMATED_UNBILL_FIGURE_CONSTANT = EstimatedUnbillFigureConstant.Table;
    const [isLoading, setIsLoading] = useState(false);
    const [estimatedUnbillFigureState] = useEstimatedUnbillFigureTracked();
    const estimatedUnbillFigureVM = useEstimatedUnbillFigureVM();
    const { editingEntity, isAdd, isEdit, masterState} = estimatedUnbillFigureState;
    const { isSaveClicked, allFormState } = masterState;
    const messageBarVM = useMessageBarVM();
    const [anaInfoState] = useANAInfoTracked();
    const { allowUpdate } = anaInfoState;

    const handleSave = useCallback(async () => {
        estimatedUnbillFigureVM.onSaveClicked();
        setIsLoading(true);
        const data = await estimatedUnbillFigureVM.onSave(editingEntity);
        if (data && data.success) {
            if (data.data) {
                setIsLoading(false)
                messageBarVM.showError(data.data);
                return;
            } else {
                messageBarVM.showSuccess("Save Successfully.");
                estimatedUnbillFigureVM.searchAllEstimatedUnbillFigureList().then((data) => {
                    setIsLoading(false)
                }).catch(error => {
                    setIsLoading(false)
                });
            }
        } else {
            setIsLoading(false)
            messageBarVM.showError(data.data);
            return;
        }
    }, [editingEntity, estimatedUnbillFigureVM, messageBarVM]);

    const handClose = useCallback(() => {
        estimatedUnbillFigureVM.onCloseEidtPanel();
    }, [estimatedUnbillFigureVM]);

    const memoYear = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!isAdd}
                isShowOptional={false}
                readOnlyValue={editingEntity?.year || ''}
                fieldValue={editingEntity?.year}
                fieldLabel={ESTIMATED_UNBILL_FIGURE_CONSTANT.YEAR}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'year'}
                maxLength={4}
                isShowMissingError={true}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    estimatedUnbillFigureVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [ESTIMATED_UNBILL_FIGURE_CONSTANT.YEAR, editingEntity?.year, isAdd, estimatedUnbillFigureVM, allFormState, isSaveClicked])

    const memoMonth = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!(isAdd || isEdit)}
                isShowOptional={false}
                readOnlyValue={editingEntity?.month || ''}
                fieldValue={editingEntity?.month}
                fieldLabel={ESTIMATED_UNBILL_FIGURE_CONSTANT.MONTH}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'month'}
                maxLength={2}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    estimatedUnbillFigureVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [ESTIMATED_UNBILL_FIGURE_CONSTANT.MONTH, editingEntity?.month, isAdd, isEdit, estimatedUnbillFigureVM, allFormState, isSaveClicked])

    const memoAmount = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!(isAdd || isEdit)}
                isShowOptional={false}
                readOnlyValue={editingEntity?.amount || ''}
                fieldValue={editingEntity?.amount}
                fieldLabel={ESTIMATED_UNBILL_FIGURE_CONSTANT.AMOUNT}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'amount'}
                maxLength={10}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    estimatedUnbillFigureVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [ESTIMATED_UNBILL_FIGURE_CONSTANT.AMOUNT, editingEntity?.amount, isAdd, isEdit, estimatedUnbillFigureVM, allFormState, isSaveClicked])

    const handleEdit = useCallback(() => {
        estimatedUnbillFigureVM.onEditClick();
    }, [estimatedUnbillFigureVM]);

    const handleReset = useCallback(() => {
        estimatedUnbillFigureVM.onResetClick(isAdd);
    }, [estimatedUnbillFigureVM, isAdd]);

    return <>
        <StyledSidebar style={{ width: '100%', height: "100%", border: 'none', backgroundColor: '#FFFFFF', overflow: 'auto', padding: '0' }} position='right'>
            <Sidebarheader style={{ width: '100%' }}>
                {isLoading && <Loader Indicator="Spinner" size="Medium" />}
                <Breadcrumb>
                    <HPHBreadcrumb breadcrumbData={[{ title: EstimatedUnbillFigureConstant.Title.CATEGORY },
                    { title: EstimatedUnbillFigureConstant.Title.TITLE }]} onCurrentClick={() => { }}>
                    </HPHBreadcrumb>
                </Breadcrumb>
                <StyledAction>
                    <>
                        {allowUpdate && (!(isAdd || isEdit)) && <NbisIconButton fileName='Icon-pen' size='medium' disabled={false} toolTipArrow={false} toolTipPlacement="bottom" toolTipText={'Edit'} onClick={handleEdit} />}
                        {(isAdd || isEdit) && <IconButton fileName="Icon-reset" size="medium" disabled={false} toolTipArrow={false} toolTipPlacement="bottom" toolTipText={'Reset'} onClick={() => handleReset()} />}
                        <IconButton fileName="Icon-cross" size="medium" disabled={false} toolTipArrow={false} toolTipPlacement='bottom' toolTipText={'Close'} onClick={() => handClose()} />
                        {(isAdd || isEdit) && <IconButton fileName="Icon-tick" size="medium" disabled={false} toolTipArrow={false} toolTipPlacement="bottom" toolTipText={'Save'} onClick={() => handleSave()} />}
                    </>
                </StyledAction>
            </Sidebarheader>

            <div className={'add-edit-form'} style={{ height: 'calc(100vh - 125px)', maxHeight: 'calc(100vh - 125px)', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>
                        <div className={'im-flex-row'}>
                            <div className={'flex-row-item-stretch'}>
                                <SidebarTitle>
                                    {isAdd && "New Estimated Unbill Figure"}
                                    {!isAdd && (editingEntity.year + "/" + editingEntity.month)}
                                </SidebarTitle>
                                {<SidebarCaption>"All fields are required unless indicated as 'optional'"</SidebarCaption>}
                            </div>
                        </div>

                        <CriteriaItemContainer>
                            {memoYear}
                        </CriteriaItemContainer>
                        <CriteriaItemContainer>
                            {memoMonth}
                        </CriteriaItemContainer>
                        <CriteriaItemContainer>
                            {memoAmount}
                        </CriteriaItemContainer>
                    </div>
                </div>
            </div>
        </StyledSidebar>
    </>
}

export default memo(EstimatedUnbillFigureEditPanel);
